// src/pages/LandingPage.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/logo.png';

const LandingPage = ({ isLoggedIn }) => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-indigo-50" />
        <div className="relative max-w-5xl mx-auto px-4 py-16 md:py-24 text-center">
          <img src={logo} alt="Luminoso" className="h-16 w-16 mx-auto mb-6 rounded-xl shadow-md" />
          <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight">
            Fundamental analysis.
            <br />
            <span className="text-brand-600">Not fundamental pricing.</span>
          </h1>
          <p className="mt-4 text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
            275+ companies. 80+ metrics. 8 interactive charts. Analyst ratings. Sector benchmarks. Earnings trends.
          </p>
          <p className="mt-2 text-2xl font-bold text-gray-900">
            $2.99<span className="text-base font-normal text-gray-500">/mo</span>
          </p>
          <p className="text-sm text-gray-400 mt-1">
            Similar tools charge $20–$40/mo
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate(isLoggedIn ? '/metrics' : '/signup')}
              className="btn-primary px-8 py-3 text-base font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all"
            >
              {isLoggedIn ? 'Go to Metrics' : 'Start Free →'}
            </button>
            <button
              onClick={() => navigate('/subscribe')}
              className="px-8 py-3 text-base font-semibold text-brand-600 bg-white border-2 border-brand-200 rounded-lg hover:border-brand-400 transition-all"
            >
              View Plans
            </button>
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="max-w-6xl mx-auto px-4 py-16">
        <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-12">
          Everything a value investor needs
        </h2>
        <div className="grid md:grid-cols-3 gap-8">
          <FeatureCard
            icon="📊"
            title="Deep Fundamentals"
            description="36 financial statement metrics, 36 advanced ratios, valuation scores, and earnings quality — all computed and ready to read."
          />
          <FeatureCard
            icon="📈"
            title="Visual Storytelling"
            description="8 interactive charts: Profitability Cascade, CFO Strength, Growth Pulse, Debt Profile, and more. See the story behind the numbers."
          />
          <FeatureCard
            icon="🎯"
            title="Market Context"
            description="Analyst consensus & price targets, sector peer comparisons, earnings trends, and treasury yield curves."
          />
          <FeatureCard
            icon="🌍"
            title="Global Coverage"
            description="NASDAQ-100, NYSE top 100, and FTSE 100. US and UK markets in one place."
          />
          <FeatureCard
            icon="📚"
            title="Learn As You Go"
            description="Every metric has plain-English definitions. Perfect for self-directed investors building their knowledge."
          />
          <FeatureCard
            icon="⚡"
            title="Instant Refresh"
            description="One click pulls the latest quarterly data. No waiting for end-of-day batch updates."
          />
        </div>
      </section>

      {/* Comparison */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-8">
            How we compare
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Feature</th>
                  <th className="text-center py-3 px-4 font-bold text-brand-600">Luminoso Pro</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-500">Others ($20-40/mo)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <ComparisonRow feature="Fundamental Metrics" us="80+" them="50-100" />
                <ComparisonRow feature="Interactive Charts" us="8" them="5-10" />
                <ComparisonRow feature="Analyst Ratings" us="✓" them="✓" />
                <ComparisonRow feature="Sector Benchmarks" us="✓" them="Some" />
                <ComparisonRow feature="Earnings Trends" us="✓" them="✓" />
                <ComparisonRow feature="Multi-exchange" us="3 (US + UK)" them="Varies" />
                <ComparisonRow feature="Learning Content" us="✓" them="Rare" />
                <ComparisonRow feature="Price" us="$2.99/mo" them="$20-40/mo" highlight />
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Tiers */}
      <section className="max-w-4xl mx-auto px-4 py-16">
        <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-4">
          Simple pricing
        </h2>
        <p className="text-center text-gray-500 mb-10">No hidden fees. Cancel anytime.</p>
        <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {/* Free */}
          <div className="border-2 border-gray-200 rounded-xl p-6">
            <h3 className="text-xl font-bold text-gray-900">Free</h3>
            <p className="text-gray-500 text-sm mt-1">Explore the basics</p>
            <p className="text-3xl font-bold mt-4">$0</p>
            <ul className="mt-4 space-y-2 text-sm text-gray-600">
              <li>✓ 2 companies (Apple & Microsoft)</li>
              <li>✓ Latest quarter only</li>
              <li>✓ 36 financial metrics</li>
              <li>✓ 2 visualisations</li>
              <li>✓ Definitions & learning</li>
            </ul>
            <button
              onClick={() => navigate('/signup')}
              className="mt-6 w-full py-2.5 text-sm font-semibold border-2 border-gray-300 rounded-lg text-gray-700 hover:border-gray-400 transition"
            >
              Get Started Free
            </button>
          </div>

          {/* Pro */}
          <div className="border-2 border-brand-500 rounded-xl p-6 relative bg-brand-50/30">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <span className="bg-brand-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                Best Value
              </span>
            </div>
            <h3 className="text-xl font-bold text-gray-900">Pro</h3>
            <p className="text-gray-500 text-sm mt-1">Everything you need</p>
            <p className="text-3xl font-bold mt-4">$2.99<span className="text-base font-normal text-gray-500">/mo</span></p>
            <p className="text-xs text-green-600 font-medium">or $29/yr (save 2 months)</p>
            <ul className="mt-4 space-y-2 text-sm text-gray-600">
              <li>✓ 275+ companies (3 exchanges)</li>
              <li>✓ 6 quarters of history</li>
              <li>✓ 80+ metrics & ratios</li>
              <li>✓ 8 interactive charts</li>
              <li>✓ Analyst ratings & price targets</li>
              <li>✓ Sector benchmarks</li>
              <li>✓ Earnings trends</li>
              <li>✓ Key Fundamentals sidebar</li>
              <li>✓ Treasury yields & curve</li>
            </ul>
            <button
              onClick={() => navigate('/subscribe')}
              className="mt-6 w-full py-2.5 text-sm font-semibold bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition shadow-md"
            >
              Upgrade to Pro →
            </button>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-gradient-to-r from-brand-600 to-indigo-700 py-12">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white">
            Stop overpaying for fundamental analysis
          </h2>
          <p className="text-brand-100 mt-2 text-lg">
            Join Luminoso and get institutional-grade metrics for the price of a coffee.
          </p>
          <button
            onClick={() => navigate(isLoggedIn ? '/metrics' : '/signup')}
            className="mt-6 px-8 py-3 bg-white text-brand-700 font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all"
          >
            {isLoggedIn ? 'Go to Dashboard' : 'Start Free Today →'}
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="max-w-5xl mx-auto px-4 py-8 text-center text-xs text-gray-400">
        <p>© {new Date().getFullYear()} Luminoso. Built for value investors.</p>
      </footer>
    </div>
  );
};

const FeatureCard = ({ icon, title, description }) => (
  <div className="text-center p-6 rounded-xl bg-white border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
    <div className="text-3xl mb-3">{icon}</div>
    <h3 className="font-bold text-gray-900 mb-2">{title}</h3>
    <p className="text-sm text-gray-600 leading-relaxed">{description}</p>
  </div>
);

const ComparisonRow = ({ feature, us, them, highlight }) => (
  <tr className={highlight ? 'bg-green-50' : ''}>
    <td className="py-3 px-4 text-gray-700">{feature}</td>
    <td className={`py-3 px-4 text-center font-semibold ${highlight ? 'text-green-700' : 'text-brand-600'}`}>{us}</td>
    <td className="py-3 px-4 text-center text-gray-500">{them}</td>
  </tr>
);

export default LandingPage;
